import { ConnectorTestOutcome, HostKeyStatus, type SftpConnectorDTO } from '@today/types'
import { Badge, Button } from '@today/ui-primitives'
import { useId } from 'react'
import { testFailureMessage } from './sftp-messages'

/** Props for {@link SftpConnectionCard}. */
export interface SftpConnectionCardProps {
	connector: SftpConnectorDTO
	busy: boolean
	error?: string
	onTest: (connectorId: string) => void
	onTrust: (connectorId: string, fingerprint: string) => void
	onDelete: (connectorId: string) => void
	onCopy: (text: string) => void
}

/** A server host key that was observed and is not the trusted one: the Super-Admin may trust it. */
const trustableKey = (c: SftpConnectorDTO) => {
	const observed = c.lastObservedHostKey
	if (!observed) return null
	return c.trustedHostKey?.fingerprint === observed.fingerprint ? null : observed
}

function TestResult({ connector }: { connector: SftpConnectorDTO }) {
	const test = connector.lastTest
	if (!test) return null
	return test.outcome === ConnectorTestOutcome.CONNECTED ? (
		<p className="text-ds-sm text-ds-success">Connected</p>
	) : (
		<p className="text-ds-sm text-ds-danger">{testFailureMessage(test.failureCode)}</p>
	)
}

function PublicKey({ publicKey, onCopy }: { publicKey: string; onCopy: (text: string) => void }) {
	return (
		<div className="flex flex-col gap-1">
			<span className="text-ds-sm font-medium text-ds-text">Public key to install for this user</span>
			<code className="break-all rounded-ds-md bg-ds-surface-muted p-ds-2 text-ds-sm text-ds-text">{publicKey}</code>
			<div>
				<Button size="sm" variant="muted" aria-label="Copy public key" onPress={() => onCopy(publicKey)}>
					Copy
				</Button>
			</div>
		</div>
	)
}

function EgressIps({ ips }: { ips: readonly string[] }) {
	return (
		<div className="flex flex-col gap-1">
			<span className="text-ds-sm font-medium text-ds-text">IP addresses to allow</span>
			<ul className="text-ds-sm text-ds-text">
				{ips.map((ip) => (
					<li key={ip}>{ip}</li>
				))}
			</ul>
		</div>
	)
}

function HostKeyTrust({
	connector: c,
	busy,
	onTrust,
}: Pick<SftpConnectionCardProps, 'connector' | 'busy' | 'onTrust'>) {
	const candidate = trustableKey(c)
	return (
		<>
			{c.trustedHostKey ? (
				<p className="text-ds-sm text-ds-text">
					Trusted host key: <code>{c.trustedHostKey.fingerprint}</code>
				</p>
			) : null}
			<TestResult connector={c} />
			{candidate ? (
				<div className="flex flex-wrap items-center gap-3">
					<span className="text-ds-sm text-ds-text">
						Server host key: <code>{candidate.fingerprint}</code>
					</span>
					<Button size="sm" isDisabled={busy} onPress={() => onTrust(c.id, candidate.fingerprint)}>
						Trust this host key
					</Button>
				</div>
			) : null}
		</>
	)
}

/**
 * One SFTP connection (TO-393 SCN-014): server, trust badge, the public key to
 * send the customer (with a copy action), the egress IPs to allowlist, the last
 * test, and the host key awaiting trust. Pure: the connector and the actions
 * are props. Never holds a private key — the DTO has none.
 */
export function SftpConnectionCard({
	connector: c,
	busy,
	error,
	onTest,
	onTrust,
	onDelete,
	onCopy,
}: SftpConnectionCardProps) {
	const headingId = useId()
	const trusted = c.hostKeyStatus === HostKeyStatus.TRUSTED
	return (
		<section aria-labelledby={headingId} className="flex flex-col gap-3 rounded-ds-md border border-ds-border p-ds-4">
			<div className="flex items-center justify-between gap-3">
				<div>
					<h4 id={headingId} className="font-medium text-ds-text">
						{c.name}
					</h4>
					<p className="text-ds-sm text-ds-text-muted">{`${c.username}@${c.host}:${c.port}`}</p>
				</div>
				<Badge variant={trusted ? 'success' : 'muted'} size="sm">
					{trusted ? 'Host key trusted' : 'Host key not trusted'}
				</Badge>
			</div>
			<PublicKey publicKey={c.publicKey} onCopy={onCopy} />
			<EgressIps ips={c.egressIpAddresses} />
			<HostKeyTrust connector={c} busy={busy} onTrust={onTrust} />
			{error ? (
				<p role="alert" className="text-ds-sm text-ds-danger">
					{error}
				</p>
			) : null}
			<div className="flex gap-2">
				<Button size="sm" variant="secondary" isDisabled={busy} onPress={() => onTest(c.id)}>
					Test connection
				</Button>
				<Button size="sm" variant="danger" isDisabled={busy} onPress={() => onDelete(c.id)}>
					Delete
				</Button>
			</div>
		</section>
	)
}
